import type { SelectionEvents } from '@fluentui/react-components';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import type { StoredSelectedItemsSchema } from '../../../types.js';

type UseOnGenericSelectionChangeParams<TData extends { allIds: string[] }> = {
    data: TData;
    selectedItems: StoredSelectedItemsSchema;
    setSelectedItems: Dispatch<SetStateAction<StoredSelectedItemsSchema>>;
};

export function useOnGenericSelectionChange<TData extends { allIds: string[] }>({
    data,
    selectedItems,
    setSelectedItems,
}: UseOnGenericSelectionChangeParams<TData>) {
    const handleSelectionChange = useCallback(
        (_: SelectionEvents, changeData: { selectedOptions: string[] }) => {
            const newSelection = new Set(selectedItems.current);
            const changedItems = new Set(
                changeData.selectedOptions
                    .filter((id) => !selectedItems.current.includes(id))
                    .concat(selectedItems.current.filter((id) => !changeData.selectedOptions.includes(id)))
            );

            // handle "Select All" special case
            if (changedItems.has('select-all')) {
                const isSelectingAll = changeData.selectedOptions.includes('select-all');

                if (isSelectingAll) {
                    // select everything
                    data.allIds.forEach((id) => newSelection.add(id));
                } else {
                    // deselect everything
                    newSelection.clear();
                }
            } else {
                // if any other item is changed, deselect "Select All"
                newSelection.delete('select-all');

                // process each changed item
                changedItems.forEach((changedId) => {
                    const isSelecting = changeData.selectedOptions.includes(changedId);

                    if (isSelecting) {
                        newSelection.add(changedId);
                    } else {
                        newSelection.delete(changedId);
                    }
                });

                // check if everything is selected to select "Select All"
                const allButSpecialIds = data.allIds.filter((id) => id !== 'select-all');
                if (allButSpecialIds.every((id) => newSelection.has(id))) {
                    newSelection.add('select-all');
                }
            }

            setSelectedItems({
                ...selectedItems,
                current: Array.from(newSelection),
            });
        },
        [data, selectedItems, setSelectedItems]
    );

    return {
        handleSelectionChange,
    };
}
